import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type ButtonVariant = 'filled' | 'tonal' | 'outlined' | 'text' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

/**
 * Material You buttons are pill-shaped and change state with an opacity overlay
 * rather than a different colour. `active:scale-95` gives the press the tactile
 * feedback the design system asks for.
 */
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap ' +
  'transition-all duration-300 ease-emphasized active:scale-95 ' +
  'disabled:pointer-events-none disabled:opacity-50'

const VARIANTS: Record<ButtonVariant, string> = {
  filled: 'bg-primary text-on-primary hover:bg-primary/90 hover:shadow-level-2 active:bg-primary/80',
  tonal:
    'bg-secondary-container text-on-secondary-container hover:bg-secondary-container/80 hover:shadow-level-1',
  outlined:
    'border border-outline text-primary bg-transparent hover:bg-primary/5 active:bg-primary/10',
  text: 'text-primary bg-transparent hover:bg-primary/10 active:bg-primary/5',
  danger: 'bg-error text-on-error hover:bg-error/90 hover:shadow-level-2 active:bg-error/80',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-5 text-label-md',
  md: 'h-10 px-6 text-label-md',
  lg: 'h-12 px-8 text-label-md',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: ReactNode
}

export function Button({
  variant = 'filled',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  )
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  tone?: 'default' | 'danger'
}

/** Icon-only affordance. `label` is required so it is never unlabelled. */
export function IconButton({
  label,
  tone = 'default',
  className,
  children,
  type = 'button',
  ...props
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-10 items-center justify-center rounded-full transition-all duration-300',
        'ease-emphasized active:scale-95 disabled:pointer-events-none disabled:opacity-50',
        tone === 'danger'
          ? 'text-error hover:bg-error/10'
          : 'text-on-surface-variant hover:bg-primary/10 hover:text-on-surface',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}
